<template>
  <div :class="ns.b()">
    <p v-if="description" :class="ns.e('desc')">
      {{ description }}
    </p>

    <z-alert
      v-if="useRemote && remoteHint"
      :title="remoteHint"
      type="info"
      show-icon
      :closable="false"
      :class="ns.e('hint')"
    />

    <TaskSummary
      :class="ns.e('summary')"
      :stats="summary"
      :active-key="filterStatus === 'all' ? undefined : filterStatus"
      @filter="handleSummaryFilter"
    />

    <TaskFilterBar
      v-model:filter-status="filterStatus"
      v-model:keyword="keyword"
      :class="ns.e('filter')"
    />

    <div v-loading="loading" :class="ns.e('main')">
      <section :class="ns.e('list')">
        <z-empty
          v-if="!loading && !filteredTasks.length"
          description="没有匹配的任务"
        />
        <TaskListItem
          v-for="task in filteredTasks"
          :key="task.id"
          :task="task"
          :selected="task.id === selectedId"
          @select="handleSelect"
          @start="handleStart"
          @complete="handleComplete"
          @ignore="handleIgnore"
          @open="handleOpen"
        />
      </section>

      <TaskDetailPanel
        :class="ns.e('detail')"
        :task="selectedTask"
        :logs="selectedLogs"
        @start="handleStart"
        @complete="handleComplete"
        @ignore="handleIgnore"
        @open="handleOpen"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useNamespace } from '@zoryn/hooks'
import ZAlert from '@zoryn/components/alert'
import ZEmpty from '@zoryn/components/empty'
import { ZMessage } from '@zoryn/components/message'
import TaskDetailPanel from './components/task-detail-panel.vue'
import TaskFilterBar from './components/task-filter-bar.vue'
import TaskListItem from './components/task-list-item.vue'
import TaskSummary from './components/task-summary.vue'
import { useTaskList } from './composables/use-task-list'
import { taskCenterEmits, taskCenterProps } from './task-center'
import { createTaskApi, mapTaskLogs } from './utils/task-api'
import { DEFAULT_MOCK_ACTION_LOGS, DEFAULT_MOCK_TASKS } from './utils/task-mock'
import { mapTaskList } from './utils/task-mapper'
import type { TaskActionLog, TaskFilterStatus, TaskRecord } from './types'

defineOptions({
  name: 'ZTaskCenter',
})

const props = defineProps(taskCenterProps)
const emit = defineEmits(taskCenterEmits)

const ns = useNamespace('task-center')

const loading = ref(false)
const useRemote = ref(false)
const remoteHint = ref('')
const actionLogs = ref<TaskActionLog[]>([])
const mockLogs = ref<TaskActionLog[]>([])

const resolvedMockTasks = computed(() =>
  props.mockTasks?.length ? props.mockTasks : DEFAULT_MOCK_TASKS
)
const resolvedMockActionLogs = computed(
  () => props.mockActionLogs ?? DEFAULT_MOCK_ACTION_LOGS
)

const taskApi = computed(() =>
  props.http ? createTaskApi(props.http, props.apiPrefix) : null
)

const {
  filterStatus,
  keyword,
  selectedId,
  summary,
  filteredTasks,
  selectedTask,
  selectTask,
  startTask,
  completeTask,
  ignoreTask,
  replaceTasks,
  setRemoteSummary,
  clearRemoteSummary,
} = useTaskList([])

const selectedLogs = computed(() => {
  if (useRemote.value) {
    return actionLogs.value
  }
  const id = selectedTask.value?.id
  if (!id) return []
  if (mockLogs.value.length) return mockLogs.value
  return resolvedMockActionLogs.value[id] ?? []
})

function shouldUseRemote() {
  if (!taskApi.value) return false
  if (props.authenticated === false) return false
  return true
}

function applyMockData(hint?: string) {
  useRemote.value = false
  clearRemoteSummary()
  replaceTasks(resolvedMockTasks.value.map((item) => ({ ...item })))
  if (hint) {
    remoteHint.value = hint
  }
}

async function loadRemoteTasks() {
  if (!taskApi.value) {
    if (props.fallbackToMock) {
      applyMockData()
    }
    return
  }

  loading.value = true
  try {
    const [dtos, summaryData] = await Promise.all([
      taskApi.value.fetchMyTasks(),
      taskApi.value.fetchTaskSummary(),
    ])
    const tasks = mapTaskList(dtos)
    replaceTasks(tasks)
    setRemoteSummary(summaryData)
    useRemote.value = true
    remoteHint.value = `已从服务端加载 ${dtos.length} 条待办`
    emit('load', tasks)
    if (selectedId.value) {
      await loadLogs(selectedId.value)
    }
  } catch (error) {
    emit('error', error as Error)
    if (props.fallbackToMock) {
      applyMockData()
      remoteHint.value = ''
      ZMessage.warning(
        (error as Error)?.message ?? '任务接口请求失败，已使用本地 mock'
      )
    } else {
      useRemote.value = false
      clearRemoteSummary()
      replaceTasks([])
      remoteHint.value = ''
      ZMessage.error((error as Error)?.message ?? '任务接口请求失败')
    }
  } finally {
    loading.value = false
  }
}

async function loadLogs(taskId: string) {
  if (!useRemote.value || !taskApi.value) {
    mockLogs.value = resolvedMockActionLogs.value[taskId] ?? []
    return
  }
  try {
    const logs = await taskApi.value.fetchTaskLogs(taskId)
    actionLogs.value = mapTaskLogs(logs)
  } catch {
    actionLogs.value = []
  }
}

async function reloadRemote() {
  if (!useRemote.value || !taskApi.value) return
  const [dtos, summaryData] = await Promise.all([
    taskApi.value.fetchMyTasks(),
    taskApi.value.fetchTaskSummary(),
  ])
  const tasks = mapTaskList(dtos)
  replaceTasks(tasks)
  setRemoteSummary(summaryData)
  emit('load', tasks)
  if (selectedId.value) {
    await loadLogs(selectedId.value)
  }
}

async function reload() {
  if (shouldUseRemote()) {
    await loadRemoteTasks()
    return
  }
  applyMockData(
    props.authenticated === false ? '未登录，展示本地 mock 数据' : undefined
  )
}

function handleSummaryFilter(status: TaskFilterStatus) {
  filterStatus.value = status
}

function handleSelect(id: string) {
  selectTask(id)
  void loadLogs(id)
}

function findTask(id: string) {
  return (
    filteredTasks.value.find((item) => item.id === id) ??
    resolvedMockTasks.value.find((item) => item.id === id)
  )
}

async function handleStart(id: string) {
  try {
    if (useRemote.value && taskApi.value) {
      await taskApi.value.startTaskApi(id)
      await reloadRemote()
    } else {
      startTask(id)
    }
    const task = findTask(id)
    if (task) emit('task-start', task)
    ZMessage.success('已开始处理')
  } catch (error) {
    ZMessage.error((error as Error)?.message ?? '操作失败')
  }
}

async function handleComplete(id: string) {
  try {
    if (useRemote.value && taskApi.value) {
      await taskApi.value.completeTaskApi(id)
      await reloadRemote()
    } else {
      completeTask(id)
    }
    const task = findTask(id)
    if (task) emit('task-complete', task)
    ZMessage.success('任务已完成')
  } catch (error) {
    ZMessage.error((error as Error)?.message ?? '操作失败')
  }
}

async function handleIgnore(id: string) {
  try {
    if (useRemote.value && taskApi.value) {
      await taskApi.value.cancelTaskApi(id)
      await reloadRemote()
    } else {
      ignoreTask(id)
    }
    const task = findTask(id)
    if (task) emit('task-ignore', task)
    ZMessage.warning('任务已忽略')
  } catch (error) {
    ZMessage.error((error as Error)?.message ?? '操作失败')
  }
}

function handleOpen(task: TaskRecord) {
  if (!task.bizTab) return
  emit('open-business', { tab: task.bizTab, task })
  const label = props.bizTabLabels[task.bizTab] ?? task.bizTab
  ZMessage.info(`正在打开「${label}」相关业务`)
}

onMounted(async () => {
  if (!props.autoLoad) {
    applyMockData()
    return
  }
  if (shouldUseRemote()) {
    await loadRemoteTasks()
    return
  }
  applyMockData(
    props.authenticated === false ? '未登录，展示本地 mock 数据' : undefined
  )
})

watch(selectedId, (id) => {
  if (id) {
    void loadLogs(id)
  }
})

defineExpose({
  reload,
})
</script>
